// src/components/screens/Settings.js
import {
  AlertTriangle,
  Lock,
  Mail,
  Phone,
  Save,
  User,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { validateForm, validators } from '../../utils/validation';
import InputField from '../common/InputField';

const Settings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  
  const [settings, setSettings] = useState({
    // Profile settings from localStorage userData
    profile: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      role: ''
    }
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user data from localStorage on component mount
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        const user = JSON.parse(storedUserData);
        setUserData(user);
        
        // Update profile settings with user data
        setSettings(prev => ({
          ...prev,
          profile: {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            mobile: user.mobile || '',
            role: user.role || ''
          }
        }));
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }

    // Load other settings from localStorage
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          system: parsed.system || prev.system,
          notifications: parsed.notifications || prev.notifications
        }));
      } catch (error) {
        console.error('Failed to load saved settings:', error);
      }
    }
  }, []);

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    handleSettingChange('profile', name, value);
    
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const saveSettings = async () => {
    setIsLoading(true);
    try {
      // Save basic settings to localStorage
      localStorage.setItem('appSettings', JSON.stringify({ lastUpdated: new Date().toISOString() }));
      
      // Note: Profile changes would need API integration to update user data
      alert('Settings saved successfully!');
    } catch (error) {
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const savePassword = async () => {
    // Validate password form
    const validationRules = {
      currentPassword: [validators.required],
      newPassword: [validators.required, validators.password],
      confirmPassword: [validators.required]
    };

    const { isValid, errors } = validateForm(passwordForm, validationRules);
    
    if (!isValid) {
      setFormErrors(errors);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setFormErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    setIsLoading(true);
    try {
      // Simulate password change - would need API integration
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setFormErrors({});
      alert('Password changed successfully!');
    } catch (error) {
      setFormErrors({ general: 'Failed to change password. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const ProfileSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 hover:shadow-lg transition-all duration-300 animate-fade-in-up">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            name="firstName"
            label="First Name"
            value={settings.profile.firstName}
            onChange={handleProfileChange}
            error={formErrors.firstName}
            icon={User}
            required
          />
          <InputField
            name="lastName"
            label="Last Name"
            value={settings.profile.lastName}
            onChange={handleProfileChange}
            error={formErrors.lastName}
            icon={User}
            required
          />
          <InputField
            name="email"
            type="email"
            label="Email Address"
            value={settings.profile.email}
            onChange={handleProfileChange}
            error={formErrors.email}
            icon={Mail}
            required
          />
          <InputField
            name="mobile"
            label="Mobile Number"
            value={settings.profile.mobile}
            onChange={handleProfileChange}
            error={formErrors.mobile}
            icon={Phone}
            required
          />
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Role
            </label>
            <input
              name="role"
              value={settings.profile.role}
              disabled
              className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white opacity-50 cursor-not-allowed"
            />
          </div>
          {userData && (
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div className={`inline-flex px-3 py-2 text-sm font-semibold rounded-full ${
                userData.isActive 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
              }`}>
                {userData.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
          )}
        </div>
        
        {userData && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Account Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">User ID</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white">{userData._id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Created</label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(userData.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(userData.updatedAt)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 hover:shadow-lg transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Security</h3>
          <Lock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        </div>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-all duration-200 flex items-center space-x-2 hover:scale-[1.03] active:scale-[0.98] shadow-sm hover:shadow-md"
        >
          <Lock className="h-4 w-4" />
          <span>Change Password</span>
        </button>
      </div>
    </div>
  );

  const SystemSettings = () => null;
  const NotificationSettings = () => null;
  const SecuritySettings = () => null;

  const renderSettingsContent = () => {
    return <ProfileSettings />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure system preferences and account settings
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={isLoading}
          className="px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-xl hover:from-primary-600 hover:to-accent-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98] flex items-center space-x-2 disabled:opacity-50 disabled:hover:scale-100"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Settings</span>
            </>
          )}
        </button>
      </div>

      {/* Settings Navigation - Removed */}
      
      {/* Settings Content - Only Profile */}
      {renderSettingsContent()}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => {
            setShowPasswordModal(false);
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setFormErrors({});
          }}
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Change Password
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setFormErrors({});
                  }}
                  className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:rotate-90"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {formErrors.general && (
                <div className="p-4 rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 flex items-center space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">{formErrors.general}</p>
                </div>
              )}

              <InputField
                name="currentPassword"
                type="password"
                label="Current Password"
                placeholder="Enter current password"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                error={formErrors.currentPassword}
                icon={Lock}
                required
              />

              <InputField
                name="newPassword"
                type="password"
                label="New Password"
                placeholder="Enter new password"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                error={formErrors.newPassword}
                icon={Lock}
                required
              />

              <InputField
                name="confirmPassword"
                type="password"
                label="Confirm New Password"
                placeholder="Confirm new password"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                error={formErrors.confirmPassword}
                icon={Lock}
                required
              />
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-end space-x-4 bg-gray-50/50 dark:bg-gray-900/50">
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setFormErrors({});
                }}
                disabled={isLoading}
                className="px-6 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={savePassword}
                disabled={isLoading}
                className="px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-xl font-medium hover:from-primary-600 hover:to-accent-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex items-center space-x-2 disabled:opacity-50 disabled:hover:scale-100"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Changing...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;